import React, { useState, useEffect, useRef, useCallback } from 'react';
import './TeamChat.css';
import GroupInfo from './GroupInfo';
import {
  connectSocket,
  getSocket,
  joinGroup,
  sendMessage,
  emitTypingStart,
  emitTypingStop,
  deleteMessage,
} from '../services/socketService';

const API_BASE = process.env.REACT_APP_API_URL || 'http://37.60.253.184:8081';

const TeamChat = ({ team, userData, token, onBack, onGroupDeleted, onGroupLeft }) => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [typingUsers, setTypingUsers] = useState([]);
  const [connected, setConnected] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [sending, setSending] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [currentTeam, setCurrentTeam] = useState(team);
  const bottomRef = useRef(null);
  const typingTimerRef = useRef(null);
  const isTypingRef = useRef(false);
  const imageInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const groupId = currentTeam.id;
  const userId = userData?.id;
  const userName = `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim() || `User ${userId}`;

  // ── Fetch message history from API ────────────────────────────────────────
  const fetchHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const res = await fetch(
        `${API_BASE}/api/chat/groups/${groupId}/messages?per_page=100`,
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );
      const data = await res.json();
      console.log('[TeamChat] History response:', res.status, data);
      if (res.ok && data.success && data.data?.data) {
        // API returns newest-first (orderBy desc), so reverse for display
        const history = [...data.data.data].reverse().map(msg => ({
          id: msg.id,
          groupId: msg.group_id,
          userId: msg.user_id,
          userName: msg.user
            ? `${msg.user.first_name || ''} ${msg.user.last_name || ''}`.trim()
            : `User ${msg.user_id}`,
          message: msg.message || '',
          messageType: msg.message_type || 'text',
          fileUrl: msg.file_url || null,
          fileName: msg.file_name || null,
          mimeType: msg.mime_type || null,
          timestamp: msg.created_at,
        }));
        setMessages(history);
      } else {
        console.warn('[TeamChat] History fetch failed or empty:', data);
      }
    } catch (err) {
      console.error('[TeamChat] Failed to load history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [groupId, token]);

  // ── Register all socket event listeners ───────────────────────────────────
  const attachListeners = useCallback((sock) => {
    sock.off('message:received');
    sock.off('typing:user');
    sock.off('message:deleted');

    sock.on('message:received', msg => {
      if (String(msg.groupId) !== String(groupId)) return;
      setMessages(prev => {
        if (prev.some(m => String(m.id) === String(msg.id))) return prev;
        return [...prev, msg];
      });
    });

    sock.on('typing:user', ({ groupId: gid, userId: tuid, userName: tuname, isTyping }) => {
      if (String(gid) !== String(groupId)) return;
      if (String(tuid) === String(userId)) return;
      setTypingUsers(prev =>
        isTyping
          ? prev.includes(tuname) ? prev : [...prev, tuname]
          : prev.filter(n => n !== tuname)
      );
    });

    sock.on('message:deleted', ({ messageId }) => {
      setMessages(prev => prev.filter(m => String(m.id) !== String(messageId)));
    });
  }, [groupId, userId]);

  // ── Connect & join room ────────────────────────────────────────────────────
  useEffect(() => {
    fetchHistory();

    const sock = connectSocket(userId, userName);

    if (sock.connected) {
      setConnected(true);
      attachListeners(sock);
      joinGroup(groupId, userId, userName);
    }

    sock.on('connect', () => {
      setConnected(true);
      attachListeners(sock);
      joinGroup(groupId, userId, userName);
    });

    sock.on('disconnect', () => setConnected(false));

    return () => {
      sock.off('connect');
      sock.off('disconnect');
      sock.off('message:received');
      sock.off('typing:user');
      sock.off('message:deleted');
    };
  }, [groupId, userId, userName, attachListeners, fetchHistory]);

  useEffect(() => {
    const sock = getSocket();
    if (sock && sock.connected) {
      setConnected(true);
      attachListeners(sock);
    }
  }, [attachListeners]);

  // ── Auto-scroll ────────────────────────────────────────────────────────────
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Send text via REST API + socket broadcast ─────────────────────────────
  const handleSend = useCallback(async () => {
    const text = inputText.trim();
    if (!text || sending) return;

    // 1. Clear input & typing immediately
    setInputText('');
    if (isTypingRef.current) {
      emitTypingStop(groupId, userId, userName);
      isTypingRef.current = false;
    }
    clearTimeout(typingTimerRef.current);

    setSending(true);
    try {
      // 2. Save to DB via REST API
      const res = await fetch(
        `${API_BASE}/api/chat/groups/${groupId}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({ message_type: 'text', message: text }),
        }
      );
      const data = await res.json();

      if (res.ok && data.success) {
        const msg = data.data.message;
        const newMsg = {
          id: msg.id,
          groupId: msg.group_id,
          userId: msg.user_id,
          userName,
          message: msg.message,
          messageType: 'text',
          fileUrl: null,
          timestamp: msg.created_at,
        };

        // 3. Add to UI (avoid duplicate if socket echoes it back)
        setMessages(prev =>
          prev.some(m => String(m.id) === String(newMsg.id)) ? prev : [...prev, newMsg]
        );

        // 4. Broadcast to other members via socket
        sendMessage({
          id: msg.id,
          groupId,
          userId,
          userName,
          message: text,
          messageType: 'text',
          timestamp: msg.created_at,
        });
      } else {
        console.error('[TeamChat] Send failed:', data.message);
        alert(data.message || 'Failed to send message.');
        setInputText(text); // restore text on failure
      }
    } catch (err) {
      console.error('[TeamChat] Send error:', err);
      alert('Failed to send message. Check your connection.');
      setInputText(text); // restore text on failure
    } finally {
      setSending(false);
    }
  }, [inputText, sending, groupId, userId, userName, token]);

  // ── Send file (image or document) via REST API ────────────────────────────
  const handleFileSend = useCallback(async (file, messageType) => {
    if (!file) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('message_type', messageType);
      formData.append('file', file);

      const res = await fetch(
        `${API_BASE}/api/chat/groups/${groupId}/messages`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: formData,
        }
      );
      const data = await res.json();

      if (res.ok && data.success) {
        const msg = data.data.message;
        const newMsg = {
          id: msg.id,
          groupId: msg.group_id,
          userId: msg.user_id,
          userName,
          message: msg.message || '',
          messageType: msg.message_type,
          fileUrl: data.data.file_url || msg.file_url || null,
          fileName: msg.file_name || file.name,
          mimeType: msg.mime_type || file.type,
          timestamp: msg.created_at,
        };
        setMessages(prev =>
          prev.some(m => String(m.id) === String(newMsg.id)) ? prev : [...prev, newMsg]
        );
      } else {
        alert(data.message || 'Failed to send file.');
      }
    } catch (err) {
      console.error('[TeamChat] File send error:', err);
      alert('Failed to send file. Please try again.');
    } finally {
      setSending(false);
    }
  }, [groupId, token, userName]);

  // ── Typing indicator ───────────────────────────────────────────────────────
  const handleInputChange = useCallback(e => {
    setInputText(e.target.value);
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      emitTypingStart(groupId, userId, userName);
    }
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      isTypingRef.current = false;
      emitTypingStop(groupId, userId, userName);
    }, 2000);
  }, [groupId, userId, userName]);

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleDelete = useCallback(msgId => {
    deleteMessage(msgId, groupId, userId);
  }, [groupId, userId]);

  // ── Key press ──────────────────────────────────────────────────────────────
  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = ts => {
    try {
      return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const getAvatarUrl = img => {
    if (!img) return null;
    if (img.startsWith('http')) {
      return img.replace('http://37.60.253.184:8081', process.env.REACT_APP_API_URL || 'http://localhost:3001')
                .replace('https://37.60.253.184:8081', process.env.REACT_APP_API_URL || 'http://localhost:3001');
    }
    return `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/${img}`;
  };

  const getProxiedUrl = (url) => {
    if (!url) return url;
    return url.replace('http://37.60.253.184:8081', process.env.REACT_APP_API_URL || 'http://localhost:3001')
              .replace('https://37.60.253.184:8081', process.env.REACT_APP_API_URL || 'http://localhost:3001');
  };

  const avatarUrl = currentTeam.group_image ? getAvatarUrl(currentTeam.group_image) : null;
  const initials = currentTeam.name?.charAt(0)?.toUpperCase() || 'G';
  const memberCount = currentTeam.members_count ?? currentTeam.members?.length ?? 0;

  const renderMessageContent = (msg) => {
    if (msg.messageType === 'image' && msg.fileUrl) {
      const src = getProxiedUrl(msg.fileUrl);
      return (
        <a href={src} target="_blank" rel="noopener noreferrer">
          <img
            src={src}
            alt="sent"
            className="tc-bubble-image"
            onError={e => { e.target.style.display = 'none'; }}
          />
        </a>
      );
    }
    if ((msg.messageType === 'file' || msg.messageType === 'voice') && msg.fileUrl) {
      return (
        <a
          href={getProxiedUrl(msg.fileUrl)}
          target="_blank"
          rel="noopener noreferrer"
          className="tc-bubble-file"
        >
          📎 {msg.fileName || 'Download file'}
        </a>
      );
    }
    return <span className="tc-bubble-text">{msg.message}</span>;
  };

  return (
    <>
      {/* ── Group Info screen ── */}
      {showGroupInfo && (
        <GroupInfo
          group={currentTeam}
          userData={userData}
          token={token}
          onBack={() => setShowGroupInfo(false)}
          onGroupDeleted={(id) => {
            setShowGroupInfo(false);
            if (onGroupDeleted) onGroupDeleted(id);
            else onBack();
          }}
          onGroupLeft={(id) => {
            setShowGroupInfo(false);
            if (onGroupLeft) onGroupLeft(id);
            else onBack();
          }}
          onGroupUpdated={(updated) => {
            setCurrentTeam(prev => ({ ...prev, ...updated }));
          }}
        />
      )}

    <div className="tc-container" style={showGroupInfo ? { display: 'none' } : {}}>
      {/* ── Header ── */}
      <div className="tc-header">
        <button className="tc-back-btn" onClick={onBack}>‹</button>

        <div
          className="tc-header-avatar"
          onClick={() => setShowGroupInfo(true)}
          style={{ cursor: 'pointer' }}
        >
          {avatarUrl ? (
            <img src={avatarUrl} alt={currentTeam.name} onError={e => { e.target.style.display = 'none'; }} />
          ) : (
            <div className="tc-header-avatar-placeholder">{initials}</div>
          )}
        </div>

        <div
          className="tc-header-info"
          onClick={() => setShowGroupInfo(true)}
          style={{ cursor: 'pointer' }}
        >
          <p className="tc-header-name">{currentTeam.name}</p>
          <p className="tc-header-sub">
            {connected ? `${memberCount} member${memberCount !== 1 ? 's' : ''}` : 'Connecting…'}
          </p>
        </div>
      </div>

      {/* ── Messages ── */}
      <div className="tc-messages">
        {loadingHistory ? (
          <div className="tc-empty">
            <p>Loading messages…</p>
          </div>
        ) : messages.length === 0 ? (
          <div className="tc-empty">
            <span>👋</span>
            <p>No messages yet. Say hello!</p>
          </div>
        ) : null}

        {messages.map(msg => {
          const isMine = String(msg.userId) === String(userId);
          return (
            <div
              key={msg.id}
              className={`tc-msg-row ${isMine ? 'tc-msg-row--mine' : 'tc-msg-row--theirs'}`}
            >
              {!isMine && (
                <div className="tc-msg-sender-name">{msg.userName}</div>
              )}
              <div className={`tc-bubble ${isMine ? 'tc-bubble--mine' : 'tc-bubble--theirs'}`}>
                {renderMessageContent(msg)}
                <span className="tc-bubble-time">{formatTime(msg.timestamp)}</span>
                {isMine && (
                  <button className="tc-delete-btn" onClick={() => handleDelete(msg.id)} title="Delete">
                    🗑
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {typingUsers.length > 0 && (
          <div className="tc-typing">
            <span className="tc-typing-dots"><span /><span /><span /></span>
            <p>{typingUsers.join(', ')} {typingUsers.length === 1 ? 'is' : 'are'} typing…</p>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* ── Input ── */}
      <div className="tc-input-row">
        {/* Hidden file inputs */}
        <input
          ref={imageInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={e => {
            const f = e.target.files[0];
            if (f) handleFileSend(f, 'image');
            e.target.value = '';
          }}
        />
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.zip"
          style={{ display: 'none' }}
          onChange={e => {
            const f = e.target.files[0];
            if (f) handleFileSend(f, 'file');
            e.target.value = '';
          }}
        />

        {/* Camera button */}
        <button
          className="tc-media-btn"
          onClick={() => imageInputRef.current?.click()}
          disabled={sending}
          title="Send image"
        >
          📷
        </button>

        {/* Attachment button */}
        <button
          className="tc-media-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={sending}
          title="Send file"
        >
          📎
        </button>

        <input
          className="tc-input"
          placeholder={connected ? 'Type a message…' : 'Connecting…'}
          value={inputText}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={sending}
        />

        <button
          className="tc-send-btn"
          onClick={handleSend}
          disabled={!inputText.trim() || sending}
        >
          {sending ? '…' : '➤'}
        </button>
      </div>
    </div>
    </>
  );
};

export default TeamChat;

